import { computed } from 'vue';
import {
  cloneDefaultMidiDrumMapping,
  DRUM_LABELS,
  normalizeDrumList,
  type DrumKey,
} from '@/config/drumMapping';
import { useSettings } from './useSettings';

const { midiDrumMapping } = useSettings();

const drumToMidis = computed<Record<DrumKey, number[]>>(() => {
  const out = {} as Record<DrumKey, number[]>;
  for (const [midiStr, drums] of Object.entries(midiDrumMapping.value)) {
    for (const drum of drums) {
      (out[drum] ??= []).push(Number(midiStr));
    }
  }
  return out;
});

export function useMidiNoteMapping() {
  const getDrumsForMidi = (midi: number): DrumKey[] => midiDrumMapping.value[midi] ?? [];

  const getDrumLabel = (midi: number): string => {
    const drums = getDrumsForMidi(midi);
    return drums.length
      ? drums.map((drum) => DRUM_LABELS[drum]).join(' + ')
      : `Unknown (${midi})`;
  };

  // Silently ignores an empty list: a mapped midi note always names at least
  // one drum, so "no drums" is a removal and goes through removeMidiDrum.
  const setMidiDrums = (midi: number, drums: DrumKey[]) => {
    const next = normalizeDrumList(drums);
    if (!next.length) return;
    midiDrumMapping.value = { ...midiDrumMapping.value, [midi]: next };
  };

  const removeMidiDrum = (midi: number) => {
    const next = { ...midiDrumMapping.value };
    delete next[midi];
    midiDrumMapping.value = next;
  };

  const getMidisForDrum = (drum: DrumKey): number[] => drumToMidis.value[drum] ?? [];

  const resetToDefaults = () => {
    midiDrumMapping.value = cloneDefaultMidiDrumMapping();
  };

  return {
    midiDrumMapping,
    drumToMidis,
    getDrumsForMidi,
    getDrumLabel,
    getMidisForDrum,
    setMidiDrums,
    removeMidiDrum,
    resetToDefaults,
  };
}
